import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useIsMutating } from 'react-query';
import { WorkflowComponentProps } from '../../Workflow/Workflow.meta';
import { hasAccessToEditProfileSelector } from '../../CommonSelectors/CommonAreaSelector';
import { useResetModuleWrapperStyles } from '../../_hooks/useResetModuleWrapperStyles';
import { ScrollHereOnce } from '../../CommonComponents/ScrollHereOnce';
import { Wrapper } from '../components/Wrapper/Wrapper';
import { ProfilePageHeader } from '../components/ProfilePageHeader/ProfilePageHeader';
import { WorkflowFooter } from '../../Workflow/components/WorkflowFooter/WorkflowFooter';
import { ErrorState } from '../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { ExpensesLoading } from './Expenses.loading';
import { StickyFooter } from '../../CommonComponents/OPL/StickyFooter/StickyFooter';
import { Button } from '../../CommonComponents/OPL/Button/Button';
import { Toggle } from '../../CommonComponents/OPL/Toggle/Toggle';
import { ExpensesTotal } from './components/ExpensesTotal/ExpensesTotal';
import { ExpensesDetailed } from './components/ExpensesDetailed/ExpensesDetailed';
import { FORM_ID } from './Expenses.meta';
import { SAVE_EXPENSES_MK } from './api/useSaveExpensesMutation';
import { ExpensesGrid } from './Expenses.styles';
import { Box } from '../../CommonComponents/OPL/Box/Box';
import { ExpensesDTO, ExpensesMode, useExpensesQuery } from './api/useExpensesQuery';

interface Props {
  clientId: string;
  workflowProps?: WorkflowComponentProps;
}

export const Expenses = ({ clientId, workflowProps }: Props) => {
  useResetModuleWrapperStyles({ isActive: !workflowProps });

  const canEdit = useSelector(hasAccessToEditProfileSelector);
  const { isLoading, isError, data } = useExpensesQuery({ clientId });

  const isSavingExpenses = useIsMutating(SAVE_EXPENSES_MK) > 0;

  return (
    <>
      <ScrollHereOnce options={false} />

      <Wrapper isWorkflow={!!workflowProps} hasStickyFooter>
        <ProfilePageHeader title="Expenses" description={data ? data.Hint : ''} />

        <ExpensesMain clientId={clientId} workflowProps={workflowProps} readOnly={!canEdit} />
      </Wrapper>

      {isLoading || isError ? (
        <StickyFooter />
      ) : workflowProps ? (
        <WorkflowFooter
          isSubmit
          form={FORM_ID}
          isFirstStep={workflowProps.isFirstStep}
          onPrevious={workflowProps.onPrevious}
          isOnNextLoading={isSavingExpenses}
        />
      ) : (
        canEdit && (
          <StickyFooter fullWidthChildren maxContentWidth={800} mt={0}>
            <Button
              type="submit"
              form={FORM_ID}
              isLoading={isSavingExpenses}
              size="large"
              ml="auto"
              mr="16"
            >
              Update Expenses
            </Button>
          </StickyFooter>
        )
      )}
    </>
  );
};

interface MainProps {
  clientId: string;
  workflowProps?: WorkflowComponentProps;
  readOnly: boolean;
}

const ExpensesMain = ({ clientId, workflowProps, readOnly }: MainProps) => {
  const { isLoading, isError, refetch, data } = useExpensesQuery({ clientId });

  if (isLoading) {
    return <ExpensesLoading />;
  }

  if (isError) {
    return <ErrorState onAction={refetch} />;
  }

  if (!data) {
    return null;
  }

  return <ExpensesContent clientId={clientId} data={data} workflowProps={workflowProps} readOnly={readOnly} />;
};

interface ContentProps {
  clientId: string;
  data: ExpensesDTO;
  workflowProps?: WorkflowComponentProps;
  readOnly: boolean;
}

const ExpensesContent = ({ clientId, data, workflowProps, readOnly }: ContentProps) => {
  const [expensesMode, setExpensesMode] = useState(data.ExpensesInputMode);

  return (
    <ExpensesGrid as="main">
      <Box css="grid-area: actions; justify-self: end" pb="4">
        <Toggle
          checked={expensesMode === ExpensesMode.Detailed}
          onChange={evt =>
            setExpensesMode(evt.target.checked ? ExpensesMode.Detailed : ExpensesMode.Total)
          }
        >
          Detailed Expenses
        </Toggle>
      </Box>

      {expensesMode === ExpensesMode.Total ? (
        <ExpensesTotal clientId={clientId} data={data} workflowProps={workflowProps} readOnly={readOnly} />
      ) : (
        <ExpensesDetailed clientId={clientId} data={data} workflowProps={workflowProps} readOnly={readOnly} />
      )}
    </ExpensesGrid>
  );
};
